import { FilledInputProps, TextField } from '@mui/material';
import { FastField, getIn } from 'formik';
import { get } from 'lodash';
import { CSSProperties, FC, InputHTMLAttributes } from 'react';
import { getFormikValueWithSubFields } from 'src/util/utils';
import { InputFieldConditionalRendering } from '../conditional-rendering/InputFieldConditionalRendering';

type InputProps = InputHTMLAttributes<HTMLInputElement>;

interface TextFieldProps {
  label: string;
  fieldName: string;
  fieldList?: string[];
  formik?: any;
  placeholder?: string;
  inputProps?: Partial<InputProps>;
  muiInputProps?: Partial<FilledInputProps>;
  disabled?: boolean;
  size?: 'small' | 'medium';
  style?: CSSProperties;
  variant?: 'standard' | 'outlined' | 'filled';
  hideHelperText?: boolean;
  type?: 'text' | 'number' | 'email' | 'password';
  autocomplete?: 'one-time-code' | 'username' | 'current-password' | 'new-password' | 'off' | 'on';
  disableFastField?: boolean;
  endAdornment?: React.ReactNode;
  margin?: 'dense' | 'normal' | 'none';
}

export const DefaultTextField: FC<TextFieldProps> = ({
  label,
  fieldName,
  placeholder,
  inputProps,
  muiInputProps,
  disabled,
  size,
  style,
  variant,
  hideHelperText,
  fieldList,
  type,
  autocomplete,
  formik,
  margin = 'normal',
}) => {
  let fieldValue: any, fieldTouched: any, fieldError: any;
  if (fieldList) {
    fieldValue = getFormikValueWithSubFields(formik.values, fieldList);
    fieldTouched = getFormikValueWithSubFields(formik.touched, fieldList);
    fieldError = getFormikValueWithSubFields(formik.errors, fieldList);
  } else {
    fieldValue = get(formik.values, fieldName) ?? '';
    fieldTouched = get(formik.touched, fieldName);
    fieldError = get(formik.errors, fieldName);
  }
  autocomplete = autocomplete ?? 'off';

  return (
    <TextField
      style={style}
      size={size}
      margin={margin}
      label={label}
      {...formik.getFieldProps(fieldName)}
      type={type}
      variant={variant ?? 'outlined'}
      fullWidth
      disabled={disabled}
      placeholder={placeholder}
      value={fieldValue}
      onChange={formik.handleChange}
      error={fieldTouched && Boolean(fieldError)}
      helperText={!hideHelperText ? (fieldTouched && fieldError) ?? ' ' : ''}
      InputProps={muiInputProps}
      autoComplete={autocomplete}
      inputProps={{ ...inputProps, 'data-cy': fieldName + '-field' }}
    />
  );
};

export const DefaultFastTextField: FC<TextFieldProps> = ({
  label,
  fieldName,
  placeholder,
  inputProps,
  muiInputProps,
  disabled,
  size,
  style,
  variant,
  hideHelperText,
  type,
  autocomplete,
  endAdornment,
}) => {
  autocomplete = autocomplete ?? 'off';
  return (
    <FastField name={fieldName}>
      {(input: any) => {
        const { field, form } = input;
        return (
          <InputFieldConditionalRendering fieldName={fieldName}>
            <TextField
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              style={style}
              size={size}
              margin="normal"
              label={label}
              type={type}
              variant={variant ?? 'outlined'}
              fullWidth
              disabled={disabled}
              placeholder={placeholder}
              error={getIn(form.touched, fieldName) && Boolean(getIn(form.errors, fieldName))}
              helperText={!hideHelperText ? (getIn(form.touched, fieldName) && getIn(form.errors, fieldName)) ?? ' ' : ''}
              inputProps={{ ...inputProps, 'data-cy': fieldName + '-field' }}
              InputProps={{ ...muiInputProps, endAdornment }}
              autoComplete={autocomplete}
              {...field}
              /* Empty string as fallback, otherwise the label won't shrink on programmatic value change. */
              value={field.value || ''}
            />
          </InputFieldConditionalRendering>
        );
      }}
    </FastField>
  );
};
